/**
 * ContributionPortal.tsx
 *
 * Main page for the Contribution Portal.
 * Displays user info, contribution actions, and user's contributed items.
 *
 * Features:
 * - Shows a hero banner with user avatar and welcome message.
 * - Displays contribution action tiles (create recipe, read guide, get help).
 * - Shows user's contributions (recipes and ingredients).
 * - Handles modal for new recipe form.
 */

import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";
import { Box, Typography, Paper, Alert, Avatar, Card } from "@mui/material";
import Authenticated from "../components/Layouts/Authenticated";
import { createDashboardStyles } from "../pages/styles";
import { getStaticAssetPath } from "../utils/staticAssets";
import FormModal from "../components/FormModal/FormModal";
import ContributionTilesSection from "../components/ContributionPortal/ContributionTilesSection";
import ManageContributionsSection from "../components/ContributionPortal/ManageContributionsSection";
import { useUserContributions } from "../hooks/useUserContributions";
import { getContributionTiles } from "../components/ContributionPortal/ContributionTilesConfig";
import { useAuthContext } from "../contexts/AuthContext";
import { useState } from "react";

// Tally Forms URLs from environment variables
const NEW_RECIPE_FORM_URL = process.env.REACT_APP_NEW_RECIPE_FORM_URL || "";

/**
 * ContributionPortal component.
 * Renders the main contribution dashboard for authenticated users.
 */
const ContributionPortal = () => {
  const theme = useTheme();
  const dashboardStyles = createDashboardStyles(theme);
  const location = useLocation();

  // Get the authenticated user from the context
  const { user } = useAuthContext();

  // Fetch only contributions for the user
  const { recipes, ingredients, loadingContributions } = useUserContributions();

  // State for the contribution form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [formUrl, setFormUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  /**
   * Generates a pre-filled URL for the contribution form and opens the modal.
   * This logic was previously in the useContributionModal hook.
   * @param baseFormUrl The base URL for the form.
   */
  const handleOpenModal = (baseFormUrl: string) => {
    setError(null);
    const params = [];
    if (user?.name) {
      params.push(`firstname=${encodeURIComponent(user.name)}`);
    }
    if (user?.last_name) {
      params.push(`lastname=${encodeURIComponent(user.last_name)}`);
    }
    const prefilledUrl =
      params.length > 0 ? `${baseFormUrl}?${params.join("&")}` : baseFormUrl;
    setFormUrl(prefilledUrl);
    setModalOpen(true);
  };

  /**
   * Closes the contribution form modal and resets the form URL.
   */
  const handleCloseModal = () => {
    setModalOpen(false);
    setFormUrl("");
  };

  // Contribution action tiles configuration
  const contributionTiles = getContributionTiles(
    theme,
    handleOpenModal,
    NEW_RECIPE_FORM_URL
  );

  return (
    <Authenticated>
      <title>Contribute - Materiom Commons</title>
      <Box
        sx={{
          ...dashboardStyles.container,
          pb: 4,
          maxWidth: 1600,
          mx: "auto",
          px: { xs: 2, md: 4 },
        }}
      >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {/* Hero banner with user info */}
        <Paper
          sx={{
            bgcolor: "transparent",
            boxShadow: "none",
            mb: 4,
          }}
        >
          <Card sx={dashboardStyles.introCard}>
            <Box sx={dashboardStyles.userInfoContainer}>
              {/* User Avatar */}
              <Box sx={dashboardStyles.userAvatarContainer}>
                <Avatar
                  src={user?.image ? getStaticAssetPath(user.image) : ""}
                  alt={user?.name || ""}
                  sx={dashboardStyles.userAvatarContainer}
                >
                  {/* Fallback: first letter of name */}
                  {user?.name ? user.name.charAt(0).toUpperCase() : ""}
                </Avatar>
              </Box>
              {/* User Text */}
              <Box sx={dashboardStyles.userTextContainer}>
                <Typography variant="body2" sx={dashboardStyles.dashboardLabel}>
                  Contribution Portal
                </Typography>
                <Typography variant="h4" sx={dashboardStyles.welcomeMessage}>
                  Contribute to the materials database
                </Typography>
              </Box>
            </Box>
          </Card>
        </Paper>

        {/* Main content: contribution tiles and user's contributions */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            borderRadius: 0.5,
          }}
        >
          <ContributionTilesSection
            tiles={contributionTiles}
            aria-label="Contribution actions"
          />
          <ManageContributionsSection
            loadingContributions={loadingContributions}
            contributions={[...recipes, ...ingredients]}
          />
        </Box>
      </Box>
      {/* Modal for contribution form */}
      <FormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        formUrl={formUrl}
        aria-label="Contribution form modal"
      />
    </Authenticated>
  );
};

export default ContributionPortal;
